import { styled, type Theme } from '@mui/material/styles'
import CheckIconBase from '@mui/icons-material/Check'
import { youShallNotPass } from 'lib/helpers/youShallNotPass/youShallNotPass'
import { STEP_MAX_WIDTH, STEP_MIN_WIDTH } from '../../StepProgress.styled'

const INDICATOR_SIZE = 18
const CONNECTOR_HEIGHT = 4
const CONNECTOR_GAP = 4
const CONNECTOR_INSET = `calc(50% + ${INDICATOR_SIZE / 2 + CONNECTOR_GAP}px)`
const STEP_GAP = 4

// A status change plays in two phases: the step being left, then the step being reached. A transition
// takes its delay from the state it moves to, so each property picks its phase from where it is heading.
const PHASE_MS = 200

interface TransitionOptions {
  theme: Theme
  property: string
  isSecondPhase: boolean
}

const getTransition = ({ theme, property, isSecondPhase }: TransitionOptions) =>
  theme.transitions.create(property, {
    duration: PHASE_MS,
    easing: theme.transitions.easing.easeOut,
    delay: isSecondPhase ? PHASE_MS : 0,
  })

const reducedMotion = {
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}

export const Item = styled('li')({
  position: 'relative',
  flex: '1 1 0',
  minWidth: STEP_MIN_WIDTH,
  maxWidth: STEP_MAX_WIDTH,
  paddingInline: STEP_GAP / 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})

type ConnectorSide = 'start' | 'end'

export const Connector = styled('span', {
  shouldForwardProp: youShallNotPass('side'),
})<{ side: ConnectorSide }>(({ theme, side }) => ({
  position: 'absolute',
  top: (INDICATOR_SIZE - CONNECTOR_HEIGHT) / 2,
  height: CONNECTOR_HEIGHT,
  overflow: 'hidden',
  backgroundColor: theme.tokens.ThemeColorCommonSeparator,
  ...(side === 'start'
    ? { left: 0, right: CONNECTOR_INSET, borderRadius: '0 2px 2px 0' }
    : { left: CONNECTOR_INSET, right: 0, borderRadius: '2px 0 0 2px' }),
}))

export const ConnectorFill = styled('span', {
  shouldForwardProp: youShallNotPass('side', 'isFilled'),
})<{ side: ConnectorSide; isFilled: boolean }>(({ theme, side, isFilled }) => ({
  display: 'block',
  height: '100%',
  backgroundColor: theme.tokens.ThemeColorCommonSecondary,
  transform: isFilled ? 'scaleX(1)' : 'scaleX(0)',
  transformOrigin: 'left',
  // The start half fills when its step is reached, the end half when its step is left.
  transition: getTransition({ theme, property: 'transform', isSecondPhase: side === 'start' ? isFilled : !isFilled }),
  ...reducedMotion,
}))

const contentStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
} as const

export const Content = styled('span')(contentStyles)

export const Button = styled('button')(({ theme }) => ({
  ...contentStyles,
  padding: 0,
  border: 'none',
  borderRadius: 4,
  background: 'transparent',
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',

  '&:focus-visible': {
    outline: `2px solid ${theme.tokens.ThemeColorCommonSecondary}`,
    outlineOffset: '2px',
  },
}))

export const Indicator = styled('span', {
  shouldForwardProp: youShallNotPass('isHighlighted'),
})<{ isHighlighted: boolean }>(({ theme, isHighlighted }) => ({
  display: 'grid',
  placeItems: 'center',
  width: INDICATOR_SIZE,
  height: INDICATOR_SIZE,
  borderRadius: '50%',
  color: theme.tokens.ThemeColorCommonOnSecondary,
  backgroundColor: isHighlighted
    ? theme.tokens.ThemeColorCommonSecondary
    : theme.tokens.ThemeColorCommonOnSurfaceTertiary,
  transition: getTransition({ theme, property: 'background-color', isSecondPhase: isHighlighted }),
  ...reducedMotion,
}))

export const StepNumber = styled('span', {
  shouldForwardProp: youShallNotPass('isVisible'),
})<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  gridArea: '1 / 1',
  fontSize: 10,
  fontWeight: 500,
  lineHeight: 1,
  opacity: isVisible ? 1 : 0,
  transition: getTransition({ theme, property: 'opacity', isSecondPhase: isVisible }),
  ...reducedMotion,
}))

export const CheckIcon = styled(CheckIconBase, {
  shouldForwardProp: youShallNotPass('isVisible'),
})<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  gridArea: '1 / 1',
  fontSize: 12,
  opacity: isVisible ? 1 : 0,
  transition: getTransition({ theme, property: 'opacity', isSecondPhase: !isVisible }),
  ...reducedMotion,
}))

export const Label = styled('span', {
  shouldForwardProp: youShallNotPass('isHighlighted'),
})<{ isHighlighted: boolean }>(({ theme, isHighlighted }) => ({
  ...Object(theme.typography.caption),
  letterSpacing: 0,
  marginTop: theme.spacing(1),
  textAlign: 'center',
  color: isHighlighted ? theme.tokens.ThemeColorCommonOnSurfacePrimary : theme.tokens.ThemeColorCommonOnSurfaceTertiary,
  transition: getTransition({ theme, property: 'color', isSecondPhase: isHighlighted }),
  ...reducedMotion,
}))

export const VisuallyHidden = styled('span')({
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
})
